// Utilities for extracting and normalizing teaser data for DS mapping

import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"
import { sanitizeInnerHtml } from "@/utils/helper"

/**
 * Strips HTML tags from a string.
 *
 * @param {string} html - The HTML string to strip.
 * @return {string} The plain text with HTML tags removed.
 */
export function stripHtml(html) {
  if (!html) return ""
  return html.replace(/<[^>]+>/g, "").trim()
}

/**
 * Determines if an item is a teaser.
 *
 * Supports both `slingResourceType` and `:type` properties.
 *
 * @param {Object} item - The item to check.
 * @return {boolean} True if the item is a teaser, false otherwise.
 */
export function isTeaser(item) {
  // Ensure a boolean is always returned
  return !!(
    item?.slingResourceType === "teaser" || item?.[":type"]?.includes("teaser")
  )
}

/**
 * Extracts and normalizes teaser data for Design System consumption.
 *
 * @param {Object} teaser - The teaser object to extract data from.
 * @param {string} [teaser.mainTitle] - The main title of the teaser.
 * @param {string} [teaser.title] - The title of the teaser.
 * @param {string} [teaser.description] - The description of the teaser.
 * @param {string} [teaser.fileReference] - The image file reference.
 * @param {Object} [teaser.imageResource] - The image resource object.
 * @param {string} [teaser.imageResource.fileReference] - The image file reference from imageResource.
 * @param {string} [teaser.imageResource.alt] - The alt text for the image.
 * @param {Array} [teaser.actions] - The array of CTA actions.
 * @param {string} [teaser.linkCTA] - The CTA link.
 * @param {string} [teaser.linkURL] - The fallback link URL.
 * @return {Object} The normalized teaser data:
 *
 */
export function extractTeaserData(teaser) {
  const imageUrl =
    teaser.fileReference || teaser.imageResource?.fileReference || ""
  const imageserverurl = teaser?.imageResource?.imageServerUrl || ""
  const imageID = teaser?.imageResource?.assetID || ""

  // Get CTA link
  let linkUrl = ""
  if (Array.isArray(teaser.actions) && teaser.actions.length > 0) {
    const action = teaser.actions.find(a => a.url || a.link)
    if (action) linkUrl = action.url || action.link
  }
  if (!linkUrl) linkUrl = teaser.linkCTA || teaser.linkURL || ""

  return {
    title: stripHtml(teaser.title || teaser.mainTitle || ""),
    description: stripHtml(teaser.description || ""),
    imageUrl,
    linkUrl,
    alt: teaser.imageResource?.alt || "",
    ctaLabel: teaser.actions?.[0]?.title || teaser.actions?.[0]?.text || "",
    tryimageUrl: `${imageserverurl}${imageID}`,
  }
}

/**
 * Maps an array of card data to the required card object structure for rendering.
 *
 * @param {Array<Object>} cards - The array of card data objects (from extractTeaserData).
 * @param {string} [pageType=""] - The page type string, used for tracking attributes in analytics.
 * @return {Array<Object>} The mapped array of card objects, each containing image, CTA, title, and description.
 */
export function mapCards(cards, pageType = "") {
  return cards.slice(0, 2).map(card => {
    const base = {
      image: {
        src: `${card.tryimageUrl}`,
        alt: card?.alt,
        "data-mode": "smartcrop",
      },
      CTA: {
        text: sanitizeInnerHtml(card?.ctaLabel),
        href: modifyHref(card?.linkUrl),
        trackingAttributes: {
          "data-datalayer-id": "kds-element-navigation",
          "data-datalayer-data": JSON.stringify({
            clickInternalLinks: "true",
            eventAction: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}:${sanitizeInnerHtml(
              card?.ctaLabel
            )?.toLowerCase()}`,
            eventName: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}:${sanitizeInnerHtml(
              card?.ctaLabel
            )?.toLowerCase()}`,
            eventType: "navigation",
            internalLinkName: sanitizeInnerHtml(card?.ctaLabel)?.toLowerCase(),
            internalLinkPosition: `${pageType}:teaser`,
            internalLinkType: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}`,
            internalLinkURL:
              card?.linkUrl && typeof window !== "undefined"
                ? `${window.location.origin}${card?.linkUrl
                    ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                    .join("")
                    .replace(".html", "")}`
                : "n/a",
            internalLinkZoneName: `${pageType}:${sanitizeInnerHtml(
              card?.title
            )?.toLowerCase()}`,
          }),
        },
      },
      title: {
        text: sanitizeInnerHtml(card?.title),
        component: "h3",
      },
      description: {
        text: sanitizeInnerHtml(card?.description),
      },
    }
    return base
  })
}
